import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, Resolver, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContactFormSchema, ContactFormValues } from "./schema";
import { editFormFields } from "./config";
import { FieldRenderer } from "../../../components/form/FieldRenderer";
import { ContactFormProps } from "./types";
import {
  getMSPContact,
  updateMSPContact,
  addMSPContact,
} from "../../msp/services/mspService";
import { addCustomerContact, getCustomerContact, updateCustomerContact } from "../../customer/services/customerService";
import { useCountryList, useStateList, useCityList } from "../../../hooks/useLocationData";
import { ContactDTO } from "../types";

const ContactForm: React.FC<ContactFormProps> = (props) => {
  const { action = "add", onSuccess, onCancel } = props;

  // Discriminator + normalized owner/contact IDs
  const isMsp = props.entity_type === "msp";
  const ownerId = isMsp ? props.mspId : props.customerId;
  const existingContactId = isMsp ? props.mspContactId : props.customerContactId;

  // fetched full contact (edit mode)
  const [fetchedContact, setFetchedContact] = useState<any | null>(null);

  const { countryList: contactCountries, isCountryLoading } = useCountryList();

  const resolver = zodResolver(ContactFormSchema) as Resolver<ContactFormValues>;

  const methods = useForm<ContactFormValues>({
    resolver,
    mode: "onBlur",
  });
  const { handleSubmit, reset, watch } = methods;

  // dependent watchers
  const contactCountryId = watch("country_id");
  const contactStateId = watch("state_id");

  const { stateList: contactStates, isStateLoading: loadingContactStates } = useStateList(contactCountryId);
  const { cityList: contactCities, isCityLoading: loadingContactCities } = useCityList(contactStateId);

  // --- Edit mode: fetch contact and reset form ---
  useEffect(() => {
    if (action !== "edit") return;
    if (!ownerId || !existingContactId) return;

    let cancelled = false;
    const fetchContact = async () => {
      try {
        const resp = isMsp
          ? await getMSPContact(ownerId, existingContactId)
          : await getCustomerContact(ownerId, existingContactId);
        const contact = resp?.data;
        if (!cancelled) setFetchedContact(contact);
      } catch (err) {
        console.error("Failed to fetch contact", err);
      }
    };
    fetchContact();
    return () => {
      cancelled = true;
    };
  }, [action, isMsp, ownerId, existingContactId]);

  // Normalize response shape
  const contactObj = useMemo(() => {
    if (!fetchedContact) return null;
    return fetchedContact;
  }, [fetchedContact]);

  // Apply fetched contact into form on edit
  useEffect(() => {
    if (action !== "edit" || !contactObj) return;

    reset((prev) => ({
      ...prev,
      first_name: contactObj.first_name ?? "",
      last_name: contactObj.last_name ?? "",
      corporate_email: contactObj.email_id ?? "",
      designation: contactObj.designation ?? "",
      job_title: contactObj.position ?? "",
      mobile_phone: contactObj.mobile_phone ?? "",
      work_phone_no: contactObj.work_phone_no ?? "",
      street_address: contactObj.address ?? "",
      country_id: contactObj.country ?? "",
      state_id: contactObj.state ?? "",
      city_id: contactObj.city ?? "",
      postal_code: contactObj.postal_code ?? "",
    }));
  }, [action, contactObj, reset]);

  // Build select options dynamically
  const updatedFields = useMemo(() => {
    return editFormFields.map((field) => {
      if (field.name === "country_id") {
        return {
          ...field,
          options: [{ label: "-- Select Country --", value: "" }, ...contactCountries.map((c: any) => ({ label: c.name, value: String(c.id) }))],
          loading: isCountryLoading,
        };
      }
      if (field.name === "state_id") {
        return {
          ...field,
          options: [{ label: "-- Select State --", value: "" }, ...contactStates.map((s: any) => ({ label: s.name, value: String(s.id) }))],
          loading: loadingContactStates,
          disabled: !contactCountryId || loadingContactStates,
        };
      }
      if (field.name === "city_id") {
        return {
          ...field,
          options: [{ label: "-- Select City --", value: "" }, ...contactCities.map((c: any) => ({ label: c.name, value: String(c.id) }))],
          loading: loadingContactCities,
          disabled: !contactStateId || loadingContactCities,
        };
      }
      return field;
    });
  }, [
    editFormFields,
    contactCountries,
    contactStates,
    contactCities,
    isCountryLoading,
    loadingContactStates,
    loadingContactCities,
    contactCountryId,
    contactStateId,
  ]);

  // Submit: create vs update depending on action & entity
  const onSubmit = async (data: ContactFormValues) => {
    try {
      const apiPayload: ContactDTO = {
        first_name: data.first_name,
        last_name: data.last_name,
        corporate_email: data.corporate_email,
        mobile_no: data.mobile_phone,
        street: data.street_address,
        designation: data.designation,
        position: data.job_title,
        contact_no: data.work_phone_no,
        country: data.country_id ? Number(data.country_id) : 0,
        state: data.state_id ? Number(data.state_id) : 0,
        city: data.city_id ? Number(data.city_id) : 0,
        zip_code: data.postal_code || "",
      };

      if (isMsp) {
        if (action === "add") {
          await addMSPContact(ownerId, apiPayload);
        } else {
          await updateMSPContact(ownerId, existingContactId!, apiPayload);
        }
      } else {
        if (action === "add") {
          await addCustomerContact(ownerId, apiPayload);
        } else {
          await updateCustomerContact(ownerId, existingContactId!, apiPayload);
        }
      }

      onSuccess?.();
    } catch (err) {
      console.error(err);
    }
  };

  const ownerLabel = isMsp ? "MSP" : "Customer";
  const title = action === "add" ? `Add ${ownerLabel} Contact` : `Edit ${ownerLabel} Contact`;
  const submitLabel = action === "add" ? "CREATE" : "UPDATE";

  return (
    <div className="w-full h-full bg-gray-50">
      <div className="mx-auto bg-white shadow-md rounded-md">
        <div className="px-6 py-4">
          <h2 className="text-xl font-semibold text-[#4F4F4F]">{title}</h2>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-6 py-6">
              <h3 className="text-lg font-medium text-[#000000] mb-4">Contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {updatedFields.map((field) => (
                  <FieldRenderer key={field.name} cfg={field} />
                ))}
              </div>
            </div>

            <div className="px-6 py-4 flex justify-center gap-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2 border border-gray-300 rounded-sm text-sm font-medium text-gray-700 bg-[#DBDBDB] focus:outline-none"
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="px-6 py-2 rounded-sm text-sm font-medium text-white bg-blue-600 focus:outline-none disabled:opacity-50"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default ContactForm;
